// src/pages/admin/PortalControlsPane.js
import { useEffect, useState } from "react";
import { db } from "../../firebaseConfig";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import ToggleSwitch from "./ToggleSwitch";

function PortalControlsPane() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const ref = doc(db, "portal_settings", "global");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setSettings(snap.data());
        } else {
          const defaults = {
            member_login_open: true,
            new_nominal_open: true,
            new_regular_open: true,
            dealer_portal_open: true,
            updated_at: new Date().toISOString(),
          };
          await setDoc(ref, defaults);
          setSettings(defaults);
        }
      } catch (err) {
        console.error("Portal load error:", err);
        setError(err.message || "Failed to load portal settings.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async (key) => {
    if (!settings) return;
    try {
      const ref = doc(db, "portal_settings", "global");
      const next = {
        ...settings,
        [key]: !settings[key],
        updated_at: new Date().toISOString(),
      };
      await updateDoc(ref, next);
      setSettings(next);
    } catch (err) {
      console.error("Portal update error:", err);
      alert("Error updating portal setting: " + err.message);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading portal settings…</div>;
  }

  if (error) {
    return (
      <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl p-4">
        ⚠ Firestore error while loading <b>portal_settings/global</b>:<br />
        <span className="text-xs font-mono">{error}</span>
        <div className="mt-2 text-xs text-slate-600">
          Check Firestore rules – current user must have read/write access to
          <code className="ml-1">portal_settings/global</code>.
        </div>
      </div>
    );
  }

  const items = [
    {
      key: "new_regular_open",
      title: "Regular Membership Registration",
      desc: "Allow new Regular Member applications with shares.",
    },
    {
      key: "new_nominal_open",
      title: "Nominal Membership Registration",
      desc: "Allow new Nominal Member applications without shares.",
    },
    {
      key: "member_login_open",
      title: "Member Login Portal",
      desc: "Enable/disable member login for document download.",
    },
    {
      key: "dealer_portal_open",
      title: "Dealer / Vendor Portal",
      desc: "Allow dealers to log in and manage products.",
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-900">
            Portal Controls
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">
            Open or close different public portals without touching code.
          </p>
        </div>
        <div className="text-[11px] text-slate-400">
          Last updated:{" "}
          <span className="font-mono">
            {settings.updated_at
              ? new Date(settings.updated_at).toLocaleString()
              : "—"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div
            key={it.key}
            className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between shadow-sm"
          >
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {it.title}
              </div>
              <div className="text-xs text-slate-500 mt-1">{it.desc}</div>
            </div>
            <ToggleSwitch
              checked={!!settings[it.key]}
              onChange={() => handleToggle(it.key)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default PortalControlsPane;
